import { useState, useEffect, createContext, useContext } from "react";
import { Redirect, useHistory } from "react-router-dom";

import {
  Container,
  Box,
  Grid,
  Typography,
  Button,
  useTheme
} from "@mui/material";

import { AnimatedLogo } from "@aclymatepackages/atoms";
import { CompanyDataConfirmation } from "@aclymatepackages/modules";
import { personalEmailDomains } from "@aclymatepackages/lists";

import SignInForm from "./SignInForm";
import ResendVerificationEmailButton from "./ResendVerificationEmailButton";

import OnboardingImageLayoutBackdrop from "../layouts/OnboardingImageLayoutBackdrop";
import CompanyDescriptionNameSearch from "../modules/CompanyDescriptionNameSearch";

import {
  fetchOurApi,
  fetchCompanyDataFromName,
  fetchNaicsCodes
} from "../../helpers/utils/apiCalls";
import { useAuth } from "../../helpers/firebase";
import { sendExistingCompanyAdminEmail } from "../../helpers/components/accountAccess";
import { analyticsTrack, triggerGoogleEvent } from "../../helpers/analytics";
import { findGoogleAddressStringDetails } from "../../helpers/utils/geography";
import { getUrlSearchParams } from "../../helpers/otherHelpers";

const OnboardingContext = createContext();

const personalEmailDomainSet = new Set(personalEmailDomains);

const isPersonalEmailWebsiteUrl = (websiteUrl) =>
  !!websiteUrl && personalEmailDomainSet.has(websiteUrl.toLowerCase());

const ExistingCompanyWithUserError = ({ existingCompanyWithUserError }) => {
  const { palette } = useTheme();

  return (
    <>
      {existingCompanyWithUserError && (
        <Grid item>
          <Typography variant="h6" color={palette.error.main} align="center">
            An account already exists for this company. We've let this account's
            administrator know that you've requested access to their account.
            You'll be informed by email once they've approved you
          </Typography>
        </Grid>
      )}
    </>
  );
};

const PageLoadingAnimation = ({ caption }) => (
  <Box display="flex" justifyContent="center">
    <Grid container spacing={2} direction="column" alignItems="center">
      <Grid item>
        <AnimatedLogo />
      </Grid>
      {caption && (
        <Grid item>
          <Typography variant="h6" align="center">
            {caption}
          </Typography>
        </Grid>
      )}
    </Grid>
  </Box>
);

const CompanyDataConfirmationStep = ({ companyData }) => {
  const history = useHistory();
  const [user] = useAuth();

  const { setVerifyAccountLoading } = useContext(OnboardingContext);

  const onConfirmClick = async (companyData = {}) => {
    const platformRedirectCallback = ({ companyId }) => {
      if (!companyId) {
        return setVerifyAccountLoading(false);
      }
      window.sessionStorage.setItem("accountId", companyId);
      triggerGoogleEvent("onboarding_finished");
      analyticsTrack("finished onboarding");
      history.push("/platform/company/onboarding");
      return window.location.reload();
    };

    const { address } = companyData;
    const { place_id, description } = address || {};
    const googleAddressStringDetails =
      await findGoogleAddressStringDetails(place_id);

    setVerifyAccountLoading(true);
    return await fetchOurApi({
      path: `/verification/verify-company-data`,
      method: "POST",
      data: {
        user,
        ...companyData,
        address: { place_id, description, ...googleAddressStringDetails }
      },
      callback: platformRedirectCallback
    });
  };

  return (
    <CompanyDataConfirmation
      companyData={companyData}
      onConfirmClick={onConfirmClick}
      fetchNaicsCodes={fetchNaicsCodes}
    />
  );
};

const OnboardingSiginIn = () => {
  const [userEmail, setUserEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accountAccessError, setAccountAccessError] = useState("");

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <Typography variant="h4" color="secondary">
          Welcome back
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Please log into your account to continue onboarding
        </Typography>
      </Grid>
      <SignInForm
        userEmail={userEmail}
        setUserEmail={setUserEmail}
        password={password}
        setPassword={setPassword}
        setAccountAccessError={setAccountAccessError}
      />
      <Grid item>
        <Typography align="center" color="error">
          {accountAccessError}
        </Typography>
      </Grid>
    </Grid>
  );
};

const ErrorMessage = ({ status = "error", email }) => {
  const statusMessages = {
    error:
      "We couldn't find a company to verify. Please make sure that you have the right URL from the email we sent you",
    expired:
      "Your verification token is expired. Click the button below to generate a new verification token"
  };

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <Typography variant="h6" align="center">
          {statusMessages[status]}
        </Typography>
      </Grid>
      <Grid item container justifyContent="center">
        <ResendVerificationEmailButton email={email} />
      </Grid>
    </Grid>
  );
};

const CompanyNameInputStep = ({ setCompanyData, setOnboardingState }) => {
  const [existingCompanyWithUserError, setExistingCompanyWithUserError] =
    useState(false);

  const onCompanyNameSearch = async (name, description) => {
    const companyData = await fetchCompanyDataFromName(name, description);

    const { hasUsers } = companyData || {};
    if (hasUsers) {
      sendExistingCompanyAdminEmail(companyData);
      return setExistingCompanyWithUserError(true);
    }

    setCompanyData(companyData);
    return setOnboardingState("confirm-info");
  };

  return (
    <Grid container spacing={2} direction="column">
      <Grid item>
        <CompanyDescriptionNameSearch
          onCompanyNameSearch={onCompanyNameSearch}
        />
      </Grid>
      <ExistingCompanyWithUserError
        existingCompanyWithUserError={existingCompanyWithUserError}
      />
    </Grid>
  );
};

const CompanyNameConfirm = ({ dbCompanyData, setOnboardingState }) => {
  const { name } = dbCompanyData || {};

  return (
    <OnboardingImageLayoutBackdrop
      title="Welcome Back"
      subtitle={`It looks like you're creating an account for ${name}. Is this right?`}
      action={
        <Grid container spacing={2} justifyContent="center" alignItems="center">
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setOnboardingState("confirm-info")}
            >
              Yes, this is my company
            </Button>
          </Grid>
          <Grid item>
            <Button
              color="primary"
              onClick={() => setOnboardingState("select-name")}
            >
              No, I'm with another company
            </Button>
          </Grid>
        </Grid>
      }
      image="/images/platform/onboarding-svg.svg"
      backdropProps={{
        backgroundColor: "rgba(255, 255, 255, 0.95)"
      }}
    />
  );
};

const CompanyOnboardingFlow = ({ dbCompanyData, setCompanyData }) => {
  const initialOnboardingState = isPersonalEmailWebsiteUrl(
    dbCompanyData?.websiteUrl
  )
    ? "select-name"
    : "confirm-name";
  const [onboardingState, setOnboardingState] = useState(
    initialOnboardingState
  );

  if (onboardingState === "confirm-name") {
    return (
      <CompanyNameConfirm
        dbCompanyData={dbCompanyData}
        setOnboardingState={setOnboardingState}
      />
    );
  }

  if (onboardingState === "confirm-info") {
    return <CompanyDataConfirmationStep companyData={dbCompanyData} />;
  }

  return (
    <CompanyNameInputStep
      setCompanyData={setCompanyData}
      setOnboardingState={setOnboardingState}
    />
  );
};

const OnboardingComponent = () => {
  const token = getUrlSearchParams("token");

  const [user, userLoading] = useAuth();
  const { uid, email } = user || {};

  const { verifyAccountLoading } = useContext(OnboardingContext);

  const [verificationStatus, setVerificationStatus] = useState(null);
  const [companyData, setCompanyData] = useState(null);

  useEffect(() => {
    const fetchVerificationStatus = async () =>
      await fetchOurApi({
        path: "/verification/check-verification-status",
        method: "POST",
        data: { uid, token },
        callback: (res) => {
          const { status, companyData } = res;

          if (status === "ready") {
            setCompanyData(companyData);
          }
          return setVerificationStatus(status);
        }
      });

    if (!userLoading && uid && !verificationStatus) {
      fetchVerificationStatus();
    }
  }, [userLoading, uid, verificationStatus, token]);

  const switchOnboardingComponents = () => {
    switch (verificationStatus) {
      case "ready":
        return (
          <CompanyOnboardingFlow
            dbCompanyData={companyData}
            setCompanyData={setCompanyData}
          />
        );
      case "redirect":
        return <Redirect to="/platform/company/dashboard" />;
      default:
        return <ErrorMessage status={verificationStatus} email={email} />;
    }
  };

  const displayContent = () => {
    if (!user && !userLoading) {
      return <OnboardingSiginIn />;
    }

    if (userLoading || !verificationStatus || verifyAccountLoading) {
      return (
        <PageLoadingAnimation
          caption={
            !!verifyAccountLoading &&
            "Please wait while we build your new account"
          }
        />
      );
    }

    return switchOnboardingComponents();
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh"
    >
      <Container maxWidth="md">{displayContent()}</Container>
    </Box>
  );
};

const OnboardingContextProvider = ({ children }) => {
  const [verifyAccountLoading, setVerifyAccountLoading] = useState(false);

  return (
    <OnboardingContext.Provider
      value={{ verifyAccountLoading, setVerifyAccountLoading }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

const Onboarding = () => (
  <OnboardingContextProvider>
    <OnboardingComponent />
  </OnboardingContextProvider>
);

export default Onboarding;
